// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useSelector } from "react-redux"

import type { RootState } from "../store/ReduxStore"

export type FlattenedPermissionsMap = Record<
  string,
  { key: string; children: string[]; parents: string[]; default: boolean }
>

export type RestructuredPermission = {
  key: string
  label: string
  default: boolean
  children?: Record<string, RestructuredPermission>
  isTopLevel: boolean
}

export type RestructuredPermissionsMap = Record<string, RestructuredPermission>

/**
 * Restructures permissions hierarchy - moves permissions with children to top level,
 * keeps childless permissions nested under their parents
 */
export function restructurePermissions(obj: object): RestructuredPermissionsMap {
  const result: RestructuredPermissionsMap = {}

  function processPermission(
    key: string,
    value: Record<string, unknown>,
    isTopLevel: boolean = true,
  ): RestructuredPermission {
    const hasChildren = value?.children && Object.keys(value.children).length > 0

    const permission: RestructuredPermission = {
      key,
      label: value.label,
      default: value.default ?? false,
      isTopLevel,
      ...(hasChildren && { children: {} }),
    }

    if (hasChildren) {
      // Process children and determine which should be at top level
      Object.entries(value.children as Record<string, Record<string, unknown>>).forEach(([childKey, childValue]) => {
        const childHasChildren = childValue?.children && Object.keys(childValue.children).length > 0

        if (childHasChildren) {
          // Permission with children goes to top level
          result[childKey] = processPermission(childKey, childValue, true)
        } else {
          // Childless permission stays under parent
          if (!permission.children) {
            permission.children = {}
          }
          permission.children[childKey] = processPermission(childKey, childValue, false)
        }
      })
    }

    return permission
  }

  Object.entries(obj).forEach(([key, value]) => {
    result[key] = processPermission(key, value, true)
  })

  return result
}

/**
 * Flattens nested permissions object into a single-level map with parent-child relationships
 */
export function PermissionsFlattener(obj: object): FlattenedPermissionsMap {
  const result: FlattenedPermissionsMap = {}

  function helper(obj: Record<string, Record<string, unknown>>, parents?: string[]): void {
    Object.entries(obj).forEach(([key, value]) => {
      // Add permission to flattened result with parent-child relationships
      result[value.label as string] = {
        key: key,
        children: [],
        parents: [...(parents ?? [])],
        default: (value.default as boolean) ?? false,
      }

      // Update parent permissions to include this child
      for (const parent of parents ?? []) {
        result[parent].children.push(value.label as string)
      }

      // Recursively process children
      if (value?.children) {
        helper(value.children as Record<string, Record<string, unknown>>, [...(parents ?? []), value.label as string])
      }
    })
  }

  helper(obj as Record<string, Record<string, unknown>>)

  return result
}

/**
 * Extracts numeric keys for all default permissions and their descendants
 * When a parent permission is default=true, includes all its children too
 */
export const collectDefaultPermissionKeys = (flattened: FlattenedPermissionsMap): string[] => {
  const selectedKeys = new Set<string>()

  Object.entries(flattened)
    .filter(([_, value]) => Boolean(value?.default))
    .forEach(([label, value]) => {
      if (flattened[label]?.key) selectedKeys.add(flattened[label].key)
      value.children?.forEach((childLabel: string) => {
        const childKey = flattened[childLabel]?.key
        if (childKey) selectedKeys.add(childKey)
      })
    })

  return Array.from(selectedKeys)
}

/**
 * Returns prefixed permission keys for form state (e.g., "sid-123" or "konan-456")
 * Uses default permissions and adds the specified prefix to each key
 */
export const getDefaultCheckedPermissionValues = (flattened: FlattenedPermissionsMap, prefix: string): string[] =>
  collectDefaultPermissionKeys(flattened).map((k) => `${prefix}-${k}`)

// permission to check on + user permissions
export const useIsPermitted = (
  permission: string,
  userPermissions: number[],
  flattenedPermissions: object,
): boolean => {
  const role = useSelector((state: RootState) => state.auth.roleName)
  const isOwner = useSelector((state: RootState) => state.auth.roleIsOwner)

  // reassigning flattenedPermissions to be able to override it with default permissions when needed
  let permissions = { ...flattenedPermissions }

  // OWNER role has all permissions
  if (isOwner) return true

  // if no permission passed or if the permissions object is empty for any reason make sure the user isnt permitted
  if (permission === undefined || Object.keys(permissions).length === 0) return false

  // default roles (USER) role, should be validated against default permissions
  // since the jwt doesn't return a permissions array for them
  if (role === "USER") {
    permissions = Object.entries(flattenedPermissions)
      // filtering by default permissions
      .filter(([_, value]) => value.default)
      // re-zipping the array into an object to be able to perform the permission check on it

      .reduce((obj, prop) => ((obj[prop[0]] = prop[1]), obj), {})
  }

  // return a boolean if the user is permitted or not
  return (
    userPermissions?.includes(parseInt(permissions[permission]?.key)) ||
    permissions[permission]?.children.some((child) => userPermissions?.includes(parseInt(permissions[child]?.key)))
  )
}
